import { useCallback, useState } from 'react';

import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import useFeature from '~/store/clinic/hooks/useFeature';
import { extractErrorMessage } from '~/utils/error/error';
import {
  IAllocationClientsByGroup,
  IClientAllocationDetailed,
} from '~/services/api/clientAllocation/types';
import AllocationTypes from '../reusable/ClientAllocationForm/constants/allocationTypes';

type UseGetClientAllocationsReturnType = {
  getClientAllocations: (payload: { actTeamId: string; allocationDate: string }) => Promise<void>;
  loading: boolean;
  clientAllocations: IClientAllocationDetailed[];
  locationGroups: IAllocationClientsByGroup[];
};

const useGetClientAllocations = (): UseGetClientAllocationsReturnType => {
  const [loading, setLoading] = useState(false);

  const { clinic: userClinic } = useStoreState(state => state.user.current);
  const clientAllocations = useStoreState(state => state.clientAllocation.current);
  const locationGroups = useStoreState(state => state.clientAllocation.locationGroups);

  const { showError } = useStoreActions(actions => actions.snackbar);
  const onGetMyClinic = useStoreActions(actions => actions.clinic.onGetMyClinic);
  const { onGetClinicActTeam, onGetActTeamsAvailable } = useStoreActions(
    actions => actions.actTeam,
  );
  const { onGetClientAllocation } = useStoreActions(actions => actions.clientAllocation);
  const {
    onGetAllocationClientsByGroup,
    onGetAllocationClientsByGroupGU,
    onGetClientAllocationGU,
  } = useStoreActions(actions => actions.clientAllocation);

  const { isCrisisAvailable } = useFeature();

  const { isGlobalUser } = useRole();

  const getClientAllocations = useCallback(
    async ({ actTeamId, allocationDate }: { actTeamId: string; allocationDate: string }) => {
      try {
        setLoading(true);
        const payload = { clinicId: String(userClinic.id), actTeamId };

        await onGetMyClinic();

        if (isGlobalUser) {
          await onGetActTeamsAvailable({ clinicId: String(userClinic.id) });
        } else {
          await onGetClinicActTeam(payload);
        }

        if (isGlobalUser) {
          await onGetClientAllocationGU({
            clinicId: String(userClinic.id),
            clientAllocationDate: allocationDate,
            allocationType: isCrisisAvailable ? AllocationTypes.ALL : AllocationTypes.STANDARD,
          });
          await onGetAllocationClientsByGroupGU({ clinicId: String(userClinic?.id) });
        } else {
          await onGetClientAllocation({
            clinicId: String(userClinic.id),
            teamId: actTeamId,
            clientAllocationDate: allocationDate,
            allocationType: isCrisisAvailable ? AllocationTypes.ALL : AllocationTypes.STANDARD,
          });
          await onGetAllocationClientsByGroup({
            clinicId: String(userClinic?.id),
            teamId: actTeamId,
          });
        }
      } catch (e) {
        showError(extractErrorMessage(e));
      } finally {
        setLoading(false);
      }
    },
    [
      userClinic.id,
      onGetMyClinic,
      isGlobalUser,
      onGetActTeamsAvailable,
      onGetClinicActTeam,
      onGetClientAllocationGU,
      onGetAllocationClientsByGroupGU,
      onGetClientAllocation,
      onGetAllocationClientsByGroup,
      showError,
      isCrisisAvailable,
    ],
  );

  return {
    getClientAllocations,
    clientAllocations,
    locationGroups,
    loading,
  };
};

export default useGetClientAllocations;
